/**
 * The field registry: the single source of truth for *which* lead fields the
 * merge engine compares and *how* each is read, compared, and written back.
 *
 * Adding a field is a data change (push a `FieldDefinition`), never a change to
 * the comparison or merge algorithms — those iterate this list generically.
 */

import {
  agentName,
  displayValue,
  humanizeKey,
  leadName,
  noteText,
  ownerName,
  pickCreatedBy,
  pickDate,
  projectName,
  valuesEqual,
} from '../helpers/merge.helpers';
import { DuplicateLead, FieldDefinition, FieldKind, LeadActivity, LeadNote, LeadRef, LeadTag, MergeEntry } from './duplicate-lead-merge.types';

let entrySeq = 0;
/** Deterministic-ish local id for a merge entry (used only client-side for de-dupe/selection). */
function makeEntryId(prefix: string, source: LeadRef, seed: string): string {
  return `${prefix}:${source._id}:${seed || ++entrySeq}`;
}

// --- scalar field factory --------------------------------------------------

function scalar(
  key: string,
  label: string,
  group: string,
  read: (lead: DuplicateLead) => any,
  toPayload?: (chosen: any, keepLead: DuplicateLead) => Record<string, any>
): FieldDefinition {
  return {
    key,
    label,
    group,
    kind: FieldKind.Scalar,
    read,
    display: displayValue,
    equals: valuesEqual,
    toPayload: toPayload ?? ((chosen: any) => ({ [key]: chosen })),
  };
}

// --- mergeable entry mappers ----------------------------------------------

function noteToEntry(prefix: string) {
  return (value: LeadNote[] | undefined, source: LeadRef): MergeEntry[] => {
    const list = Array.isArray(value) ? value : [];
    return list
      .map(n => {
        const text = noteText(n);
        if (!text) return null;
        return {
          id: makeEntryId(prefix, source, n._id ?? text),
          text,
          createdAt: pickDate(n.created_at, n.createdAt),
          createdBy: pickCreatedBy(n.created_by ?? n.createdBy),
          source,
          raw: n,
        } as MergeEntry;
      })
      .filter((e): e is MergeEntry => e !== null);
  };
}

function activityToEntry(prefix: string) {
  return (value: LeadActivity[] | undefined, source: LeadRef): MergeEntry[] => {
    const list = Array.isArray(value) ? value : [];
    return list
      .map(a => {
        const text = (a.message ?? a.description ?? a.action ?? a.type ?? '').toString().trim();
        if (!text) return null;
        return {
          id: makeEntryId(prefix, source, a._id ?? text),
          text,
          createdAt: pickDate(a.created_at, (a as any).createdAt),
          createdBy: pickCreatedBy(a.created_by),
          source,
          raw: a,
        } as MergeEntry;
      })
      .filter((e): e is MergeEntry => e !== null);
  };
}

function tagToEntry(value: LeadTag[] | undefined, source: LeadRef): MergeEntry[] {
  const list = Array.isArray(value) ? value : [];
  return list
    .map(t => {
      const text = typeof t === 'string' ? t.trim() : (t.tag_name ?? t.name ?? '').toString().trim();
      if (!text) return null;
      return {
        id: makeEntryId('tag', source, typeof t === 'string' ? t : (t._id ?? text)),
        text,
        source,
        raw: t,
      } as MergeEntry;
    })
    .filter((e): e is MergeEntry => e !== null);
}

/** Content-based key so the same note/tag text from different leads de-dupes. */
const textKey = (e: MergeEntry) => `t:${e.text.toLowerCase().replace(/\s+/g, ' ').trim()}`;

/**
 * report_notes is either a rich-text string or an array of note objects.
 * Normalise both to entries so it merges (append) instead of replacing.
 */
function reportNotesToEntry(value: any, source: LeadRef): MergeEntry[] {
  if (Array.isArray(value)) return noteToEntry('report_note')(value, source);
  const text = (value ?? '').toString().trim();
  if (!text) return [];
  return [{ id: makeEntryId('report_note', source, text), text, source, raw: value }];
}

// --- registry --------------------------------------------------------------

const SCALAR_FIELDS: FieldDefinition[] = [
  scalar(
    'name',
    'Name',
    'Details',
    l => leadName(l),
    (chosen: any) => {
      // `chosen` carries the source lead's name parts so we don't half-copy a name.
      if (chosen && typeof chosen === 'object') {
        return { first_name: chosen.first_name, last_name: chosen.last_name, full_name: chosen.full_name };
      }
      return { full_name: chosen };
    }
  ),
  // Leads store the mobile as `mobile_phone`; `mobile` is only kept for older records.
  scalar(
    'mobile',
    'Mobile',
    'Details',
    l => l.mobile_phone ?? l.mobile,
    (chosen: any) => ({ mobile_phone: chosen })
  ),
  scalar('email', 'Email', 'Details', l => l.email),
  scalar('lead_source', 'Source', 'Details', l => l.lead_source),
  // Display the project/agent NAME but copy the underlying id (+ name sibling) as payload.
  scalar(
    'project',
    'Project',
    'Details',
    l => projectName(l),
    (chosen: any) => (chosen && typeof chosen === 'object' ? chosen : { project: chosen })
  ),
  scalar(
    'agent_responsible',
    'Assigned Agent',
    'Details',
    l => agentName(l),
    (chosen: any) => (chosen && typeof chosen === 'object' ? chosen : { agent_responsible: chosen })
  ),
  // Owner id + display name + team flag travel together (see payloadValueFor); copying only the
  // name would leave the lead pointing at the old owner.
  scalar(
    'owner',
    'Owner',
    'Details',
    l => ownerName(l),
    (chosen: any) => (chosen && typeof chosen === 'object' ? chosen : {})
  ),
  scalar('lead_status', 'Lead Status', 'Details', l => l.lead_status),
  scalar('lead_type', 'Lead Type', 'Details', l => l.lead_type),
  scalar('enquiry_type', 'Enquiry Details', 'Details', l => l.enquiry_type ?? l.enquiry_details),
  scalar('requirements', 'Requirements', 'Requirements', l => l.requirements),
  scalar('follow_up', 'Follow-up', 'Engagement', l => l.follow_up),
  scalar('appointment', 'Appointment', 'Engagement', l => l.appointment),
];

const MERGEABLE_FIELDS: FieldDefinition[] = [
  {
    key: 'notes',
    label: 'Notes',
    group: 'History',
    kind: FieldKind.Mergeable,
    read: l => l.notes,
    toEntries: noteToEntry('note'),
    entryKey: textKey,
    toPayload: (mergedRaw: any[]) => ({ notes: mergedRaw }),
  },
  {
    key: 'report_notes',
    label: 'Report Notes',
    group: 'History',
    kind: FieldKind.Mergeable,
    read: l => l.report_notes,
    toEntries: reportNotesToEntry,
    entryKey: textKey,
    // If the kept lead stored report_notes as a string, keep it a string (joined);
    // otherwise write the merged array of note objects.
    toPayload: (mergedRaw: any[], keepLead: DuplicateLead) => {
      if (Array.isArray(keepLead.report_notes) || keepLead.report_notes == null) {
        const allArrays = mergedRaw.every(r => r && typeof r === 'object');
        if (allArrays) return { report_notes: mergedRaw };
      }
      const text = mergedRaw
        .map(r => (typeof r === 'string' ? r : noteText(r)))
        .filter(Boolean)
        .join('\n\n');
      return { report_notes: text };
    },
  },
  {
    key: 'tags',
    label: 'Tags',
    group: 'History',
    kind: FieldKind.Mergeable,
    read: l => l.tags,
    toEntries: tagToEntry,
    entryKey: textKey,
    toPayload: (mergedRaw: any[]) => ({ tags: mergedRaw }),
  },
  {
    key: 'activities',
    label: 'Activity History',
    group: 'History',
    kind: FieldKind.Mergeable,
    read: l => l.activities,
    toEntries: activityToEntry('activity'),
    entryKey: (e: MergeEntry) => `${textKey(e)}|${e.createdAt ?? ''}`,
    toPayload: (mergedRaw: any[]) => ({ activities: mergedRaw }),
  },
  {
    key: 'enquiry_history',
    label: 'Enquiry History',
    group: 'History',
    kind: FieldKind.Mergeable,
    read: l => l.enquiry_history,
    toEntries: activityToEntry('enquiry'),
    entryKey: (e: MergeEntry) => `${textKey(e)}|${e.createdAt ?? ''}`,
    toPayload: (mergedRaw: any[]) => ({ enquiry_history: mergedRaw }),
  },
];

export const DUPLICATE_MERGE_FIELDS: FieldDefinition[] = [...SCALAR_FIELDS, ...MERGEABLE_FIELDS];

/** The fields that identify the person, which can also be saved on the contact as alternative details. */
export const CONTACT_FIELD_KEYS: Set<string> = new Set<string>(['name', 'email', 'mobile']);

/** Every logical + payload key the registry touches — used to detect custom fields. */
export const KNOWN_FIELD_KEYS: Set<string> = new Set<string>([
  ...DUPLICATE_MERGE_FIELDS.map(f => f.key),
  // raw keys the definitions read/write beyond their logical key
  'first_name',
  'last_name',
  'full_name',
  'mobile_phone',
  'Owner',
  'Owner_formatted',
  'isOwnerTeam',
  'owner',
  'agent_responsible_formatted',
  'isAgentResponsibleTeam',
  'listing',
  'property',
  'enquiry_details',
]);

/** Build a scalar definition on the fly for a discovered custom field. */
export function customScalarField(key: string): FieldDefinition {
  return scalar(key, humanizeKey(key), 'Custom Fields', l => l[key]);
}
